import { useCallback, useEffect } from 'react';
import styled from '@emotion/styled/macro';
import { useDispatch, useSelector } from 'react-redux';
import { Button, notification, Popconfirm, Tooltip } from 'antd';
import { Link, useHistory, useParams } from "react-router-dom";
import { Typography } from 'antd';

import { selectUserData } from 'store/user/userSelector';
import UserRole from 'core/shared/enum/UserRole';
import { selectAccountManagerById, selectSetEnableAccountManagerStatus, selectDeleteAccountManagerStatus } from 'store/account-manager/accountManagerSelector';
import RequestStatus from 'core/shared/enum/RequestStatus';
import { deleteAccountManager, getAccountManagerById, setEnableAccountManager } from 'store/account-manager/accountManagerThunk';
import { resetDeleteAccountManagerStatus, resetGetAccountManagerById, resetSetEnableAccountManagerStatus } from 'store/account-manager/accountManagerSlice';
import PageContainer from 'components/layouts/PageContainer';

const { Title } = Typography;

const AccountManagersProfileScreen: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { id } = useParams<{ id: string }>()

  const userData = useSelector(selectUserData);

  const accountManager = useSelector(selectAccountManagerById);
  const setEnableAccountManagerStatus = useSelector(selectSetEnableAccountManagerStatus);
  const deleteAccountManagerStatus = useSelector(selectDeleteAccountManagerStatus);

  const isSetEnableAccountManagerLoading = setEnableAccountManagerStatus === RequestStatus.LOADING;
  const isSetEnableAccountManagerSuccess = setEnableAccountManagerStatus === RequestStatus.SUCCEESS;
  const isDeleteSuccess = deleteAccountManagerStatus === RequestStatus.SUCCEESS;

  const hasDoubleRole = accountManager && accountManager.roles.length > 1 && accountManager.active;

  const setEnableHandler = async (id: string, isActive: boolean) => {
    dispatch(setEnableAccountManager(id, isActive))
  };

  useEffect(() => {
    if (isSetEnableAccountManagerSuccess) {
      notification.success({
        message: `Success`,
        description: hasDoubleRole ? 'Account Manager successfully deleted' : 'Account manager status changed successfully.',
        placement: 'bottomRight',
      });
      dispatch(resetSetEnableAccountManagerStatus());
      if (hasDoubleRole) {
        history.goBack();
      } else {
        dispatch(getAccountManagerById(id));
      }
    }
  }, [dispatch, setEnableAccountManagerStatus, isSetEnableAccountManagerSuccess, id, hasDoubleRole, history]);

  useEffect(() => {
    if (isDeleteSuccess) {
      notification.success({
        message: `Success`,
        description: 'Account Manager successfully deleted',
        placement: 'bottomRight',
      });
      dispatch(resetDeleteAccountManagerStatus());
      history.goBack();
    }
  }, [dispatch, history, isDeleteSuccess]);

  useEffect(() => {
    dispatch(getAccountManagerById(id));
    return () => {
      dispatch(resetGetAccountManagerById());
    }
  }, [dispatch, id]);

  const statusText = hasDoubleRole ? 
  `The account manager proposed for deactivation also has a client role. Deactivation of this account manager will remove the account manager role from the associated e-mail account. Are you sure that you want to remove the role?` : 
  `Are you sure to ${accountManager?.active ? 'deactivate' : 'activate'} this account manager?`;

  const statusOkText = hasDoubleRole ? 'Remove' : 'Yes';

  const statusCancelText = hasDoubleRole ? 'Cancel' : 'No';

  const onClose = useCallback(() => {
    if (history.length < 3) {
      history.push("/account-managers");
    } else {
      history.goBack();
    }
  }, [history]);

  return (
    <PageContainer title="Account Manager Profile">
      {accountManager && (
        <>
          <Profile>
            <Title level={3}>Account Manager: <b>{accountManager?.email}</b></Title>
            <Line>
              <Label>Email (username):</Label>
              <Info>{accountManager.email || '<No info>'}</Info>
            </Line>
            <Line>
              <Label>First name:</Label>
              <Info>{accountManager.firstName || '<No info>'}</Info>
            </Line>
            <Line>
              <Label>Last name:</Label>
              <Info>{accountManager.lastName || '<No info>'}</Info>
            </Line>
            <Line>
              <Label>Managed organizations:</Label>
              <Info>{accountManager.organizationsManagedBy.map(item => item.name).join(', ') || '<No info>'}</Info>
            </Line>
            <Line>
              <Label>Status:</Label>
              <Info><ActiveData isActive={!!accountManager.active}>{!!accountManager.active ? 'Active' : 'Not active'}</ActiveData></Info>
            </Line>
            <Line>
              <Label>Contact Address:</Label>
              <Info>{accountManager.contactAddress || 'No info'}</Info>
            </Line>
            <Line>
              <Label>Phone Number:</Label>
              <Info>{accountManager.phoneNumber || 'No info'}</Info>
            </Line>
          </Profile>
          <ButtonContainer>
            {userData.userRole === UserRole.ROLE_SUPER_USER && (
              <>
                <Link style={{ margin: '0 10px' }} to={`${accountManager?.id}/update`}>
                  <Button type="primary" size="middle">Update</Button>
                </Link>
                <Popconfirm
                  title={`Are you sure to delete this account manager?`}
                  onConfirm={() => dispatch(deleteAccountManager(accountManager.id))}
                  okText="Yes"
                  cancelText="No"
                  disabled={!!accountManager.active || !!accountManager.organizationsManagedBy.length}
                >
                  <Tooltip
                    title={!!accountManager.active || !!accountManager.organizationsManagedBy.length ? "An Account Manager profile can be deleted if it was previously deactivated and if it does not have any managed organization." : ""}
                    trigger="hover"
                    color="white"
                    overlayInnerStyle={{ color: "#000000d9", padding: "12px 16px", textAlign: "justify" }}
                  >
                    <Button
                      type="primary"
                      size="middle"
                      style={{ margin: '0 10px' }}
                      danger
                      disabled={!!accountManager.active || !!accountManager.organizationsManagedBy.length}
                    >
                      Delete
                      </Button>
                  </Tooltip>
                </Popconfirm>
                <Popconfirm
                  title={<div style={{ width: '350px' }}>{statusText}</div>}
                  onConfirm={() => setEnableHandler(accountManager.id, !accountManager.active)}
                  okText={statusOkText}
                  cancelText={statusCancelText}
                >
                  <Button
                    loading={isSetEnableAccountManagerLoading}
                    type="primary"
                    size="middle"
                    style={{ margin: '0 10px', backgroundColor: '#52C41A', color: 'white' }}
                  >
                    {accountManager?.active ? 'Deactivate' : 'Activate'}
                  </Button>
                </Popconfirm>
              </>
            )}
            <Button type="default" size="middle" style={{ margin: '0 10px' }} onClick={onClose}>Close</Button>
          </ButtonContainer>
        </>
      )}
    </PageContainer>
  );
};

export default AccountManagersProfileScreen;

const Profile = styled.div`
  width: 400px;
  margin: 0 auto 15px;
  display: flex;
  flex-direction: column;
`;

const ActiveData = styled.span<{ isActive: boolean }>`
  color: ${({ theme, isActive }) => (isActive ? '#52C41A' : '#FF4D4F')}; 
`;

const ButtonContainer = styled.div`
  max-width: 400px;
  margin: 0 auto;
  display: flex;
  justify-content: flex-end;
  margin: 10px auto;
`;

const Line = styled.div`
  display: flex;
`;

const Label = styled.div`
  width: 150px;
  font-weight: bold;
  line-height: 22px;
  border-bottom: 1px solid #F0F0F0;
  margin-right: 10px;
  padding: 10px 0 10px;
  text-align: right;
`;

const Info = styled.div`
  width: 240px;
  text-align: left;
  line-height: 22px;
  border-bottom: 1px solid black;
  padding: 10px 0 10px;
  border-bottom: 1px solid #F0F0F0;
  max-width: 240px;
  line-break: auto;
  word-break: break-word;
`;
